/**
 * The three MotionPace changes to the homepage (#545), in one gated module.
 *
 * WHY A SEPARATE MODULE AT ALL
 * /motionpace (#544) could be gated by simply not declaring its <Route>, because
 * the whole page is one component. The homepage cannot: LandingPage.js ships in
 * every build, so any MotionPace markup, copy, CSS or video written inline there
 * would sit in production's main.js — readable by anyone who opens the bundle —
 * even with the flag dark. Collecting all three changes here means LandingPage
 * has exactly one `require` to gate, and a production build never pulls in this
 * file, its stylesheet, or the hero clip.
 *
 * LandingPage.js branches on the RAW `process.env.REACT_APP_MOTIONPACE_MARKETING`
 * literal, which is what lets CRA's DefinePlugin fold the branch to `false` and
 * lets webpack drop this module from the graph. See the comment at that call
 * site, and src/__tests__/motionPaceGating.test.js for the assertions.
 *
 * Copy here is the approved comp 20 (~/Projects/tweedia-comps/20-homepage-motionpace)
 * verbatim, and inherits #544's rules: brand is MotionPace, the plain descriptor
 * is "adaptive pacing", and no claim is made about the speed dial's default.
 */

import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';

// Imported through webpack, NOT dropped in public/. Files in public/ are copied
// into every build verbatim, so a production deploy would serve the unlaunched
// MotionPace render at a guessable URL. Importing routes it through the gate.
//
// The poster goes through the same gate for the same reason. It was re-cut from
// frame 0 of the clip beside it (#582) — the previous file was STALE, left over
// from a retired render of a different build phase, which is why the cells used
// to fall back to a shared poster instead.
import heroAfterClip from './motionpace/hero-motionpace-after.mp4';
import heroAfterPoster from './motionpace/hero-motionpace-after-poster.jpg';

// The chart+strip visual, shared with /motionpace so the SVG isn't duplicated.
import MotionChartHero from '../motionpace/MotionChartHero';

// Shared with the flag-off hero and /motionpace: all three autoplay on
// prerendered routes and hit the same `muted`-is-property-only hole (#582).
import forceMuted from '../../lib/forceMuted';

// Baked day-map gradient CSS strings (derived from real motion series).
import {
  CONSTANT_MAP_CSS,
  TRIM_MAP_CSS,
  PACE_MAP_CSS,
} from '../motionpace/sourceTreatmentMapData';

import './motionPaceHome.css';

/** Analytics id for the section's only outbound link. */
export const LEARN_MORE_CTA_ID = 'homepage_motionpace_learn_more';

/** Section id for the homepage's IntersectionObserver view tracking. */
export const SECTION_ID = 'motionpace';

// Real durations from ffprobe (floored to match native control bar display):
//   landing-proof-before.mp4  → 29.10s → "0:29"
//   landing-proof-after.mp4   → 12.67s → "0:12"
//   hero-motionpace-after.mp4 → 12.63s → "0:12"
const DUR_BEFORE = '0:29';
const DUR_TRIM = '0:12';
const DUR_PACE = '0:12';

// One poster per hero cell, each cut from frame 0 of the clip it sits on (#582).
// Before this, all three cells pointed at the constant clip's poster, so two of
// them showed a still that was not their own first frame — and the moment
// playback took over, those two visibly jumped. Frame 0 rather than a prettier
// mid-clip still, for the same reason: the poster must be the frame the clip
// starts on, so nothing pops on transition. This matters whenever playback has
// not started yet — reduced motion, a slow connection, or the autoplay refusal
// `forceMuted` fixes.
//
// WHAT THIS DOES NOT MEAN: three posters is not three different photographs.
// Cells 1 (constant) and 3 (MotionPace) are the same instant of the same day —
// measured 32.5 dB PSNR apart, and that gap is almost entirely the speed dial
// burned into the MotionPace clip's corner. That is CORRECT and deliberate:
// MotionPace keeps every moment, so its frame 0 *is* the constant clip's frame
// 0, and cutting a different-looking still to make the 3-up prettier would be
// claiming a difference the treatment does not produce. Only the MotionTrim
// poster (cell 2) is a genuinely different frame (18 dB), because trimming
// drops the opening still stretch. So the win here is per-cell honesty and no
// poster-to-playback pop, NOT visual variety.
//
// Note that the accompanying tests compare poster FILENAMES, not pixels — they
// cannot see the above and will not warn either way. Re-measure by hand (ffmpeg
// frame 0 vs the .jpg) if you ever re-cut a clip.
//
// The MotionPace cell's poster is the `heroAfterPoster` import, not a path here,
// because it is a gated asset that must not sit in public/.
const POSTER_CONSTANT = '/landing-proof-before-poster.jpg';
const POSTER_TRIM = '/landing-proof-after-poster.jpg';

/**
 * A slim "source treatment map" bar — left to right = the same source day.
 * Shows how constant / MotionTrim / MotionPace each treats the day's minutes.
 *
 * Purely presentational: all geometry is baked as CSS gradient strings in
 * sourceTreatmentMapData.js (no runtime path parsing).
 */
function SourceTreatmentMap({ css, label, teal }) {
  return (
    <div className="stmap-foot">
      <div
        className="stmap-bar"
        style={{ backgroundImage: css }}
        aria-hidden="true"
      />
      <span className={`stmap-label${teal ? ' stmap-label-teal' : ''}`}>{label}</span>
    </div>
  );
}

/**
 * The triple-comparison hero: constant / MotionTrim / MotionPace, all showing
 * the same first frame from the same shoot, side by side.
 *
 * Design spec: mockup2a-hero-triple.png + mockup2a-hero-triple.html in
 * qa/motionpace-landing-mockups/. Approved 2026-09-03.
 *
 * VIDEO SYNC: all three autoplay muted together on mount, no native loop. When
 * the longest clip (constant, 0:29) fires `ended`, all three are reset to
 * currentTime=0 and replayed — they never drift because the sync event comes
 * from the longest clip.
 */
export function TripleHero({ reduceMotion, showProofControls }) {
  const [autoplayBlocked, setAutoplayBlocked] = useState(false);
  const showControls = reduceMotion || showProofControls || autoplayBlocked;

  // All three videos in one ref array so the sync handler can reach them.
  //
  // Nothing is shed on the `null` detach call, deliberately. A filter on
  // `v.isConnected` was tried here and removed: probed on React 18.3.1, a
  // re-keyed <video> logs `attach(a) / detach(null) / attach(b)`, and at that
  // null call the outgoing node still reports `isConnected: true` — React has
  // not removed it from the document yet — so the filter kept exactly what it
  // was meant to drop. Any future shed has to run somewhere the node really is
  // detached (effect cleanup, or a queueMicrotask off the null call).
  //
  // `includes` is cheap insurance against a double-attach, not a live fix: the
  // same probe shows StrictMode's dev double-render fires the ref only ONCE on
  // 18.3.1 (`render / render / attach`), so today nothing can register twice.
  const videosRef = useRef([]);
  const registerVideo = useCallback((el) => {
    if (!el) return;
    // Before the push, so it runs even on a re-attach of an already-registered
    // node. See forceMuted: the JSX `muted` prop alone does not survive the
    // prerender snapshot, and hydration never restores it.
    forceMuted(el);
    if (!videosRef.current.includes(el)) videosRef.current.push(el);
  }, []);

  // The constant cell is the longest clip (0:29), so it owns the loop-sync
  // event. Which cell that is, is hard-coded rather than measured: jsdom has no
  // media engine, so `duration` is NaN in tests, and the three clips are fixed
  // assets whose lengths are already spelled out in DUR_* above.
  const constantElRef = useRef(null);
  const constantRef = useCallback(
    (el) => {
      registerVideo(el);
      constantElRef.current = el;
    },
    [registerVideo]
  );

  // Sync: when the LONGEST video ends, reset all to frame 0 and replay.
  // `ended` on the two shorter clips is never listened for — they hold their
  // last frame until the long one ends and the cycle restarts, so the three
  // can never drift.
  const handleEnded = useCallback(() => {
    videosRef.current.forEach((el) => {
      // eslint-disable-next-line no-param-reassign
      el.currentTime = 0;
      el.play?.()?.catch?.(() => {});
    });
  }, []);

  // Autoplay all three, then wire the loop-sync listener to the constant cell.
  // The listener is attached HERE rather than in the ref callback so that it
  // gets a matching removeEventListener: a ref callback has no cleanup phase,
  // so if its identity ever changed (say `reduceMotion` became reactive instead
  // of a one-shot useState initialiser) a second listener would stack on the
  // same element and every `ended` would fire the reset twice.
  useEffect(() => {
    if (reduceMotion) {
      // NOT a bare early-out, and `forceMuted` is why. `/` is prerendered with
      // motion allowed, so the shipped HTML carries `autoplay` on all three
      // cells and no `controls`; hydration warns about mismatched attributes
      // without repairing them, and `reduceMotion` is a one-shot useState
      // initialiser that never re-renders. The missing `muted` attribute used
      // to block that parse-time autoplay by accident — Chrome refuses an
      // unmuted cold start — so a reduced-motion visitor got three posters for
      // the wrong reason. `registerVideo` now puts `muted` in the snapshot, so
      // the parser starts all three before React loads and the stop has to be
      // explicit and imperative (#582).
      //
      // Here rather than in `registerVideo`: that callback is
      // `useCallback(..., [])` on purpose (a stable identity, so a re-attach
      // cannot stack listeners) and so has no `reduceMotion` to branch on.
      videosRef.current.forEach((el) => {
        // eslint-disable-next-line no-param-reassign
        el.autoplay = false;
        // These cells carry no `loop` — the constant cell's `ended` handler
        // drives the sync replay instead — so this is belt-and-braces against
        // a future loop prop, not a live fix.
        // eslint-disable-next-line no-param-reassign
        el.loop = false;
        // `showControls` reaches the JSX only, which the hydrated snapshot
        // never picks up; without this the visitor gets no pause mechanism.
        // eslint-disable-next-line no-param-reassign
        el.controls = true;
        el.pause?.(); // the prerendered HTML may already have started it
      });
      return undefined;
    }
    videosRef.current.forEach((el) => {
      el.play?.()?.catch?.(() => setAutoplayBlocked(true));
    });
    const constant = constantElRef.current;
    if (!constant) return undefined;
    constant.addEventListener('ended', handleEnded);
    return () => constant.removeEventListener('ended', handleEnded);
  }, [reduceMotion, handleEnded]);

  return (
    <div className="hero-triple-grid" data-testid="hero-triple-grid">
      {/* Cell 1: Constant — raw camera */}
      <div className="hero-proof-cell hero-triple-cell">
        <div className="hero-triple-media">
          <span className="hero-proof-tag hero-proof-tag-before">
            Straight off the camera
          </span>
          <video
            ref={constantRef}
            src="/landing-proof-before.mp4"
            poster={POSTER_CONSTANT}
            autoPlay={!reduceMotion}
            muted
            playsInline
            controls={showControls}
            preload="metadata"
            aria-label={`One day of a deck build straight off the camera, ${DUR_BEFORE}`}
            data-testid="hero-triple-constant-video"
          />
          {!showControls && (
            <span className="hero-proof-dur" data-testid="hero-triple-dur-before">
              {DUR_BEFORE}
            </span>
          )}
        </div>
        <SourceTreatmentMap
          css={CONSTANT_MAP_CSS}
          label="every minute, same speed"
        />
      </div>

      {/* Cell 2: MotionTrim */}
      <div className="hero-proof-cell hero-triple-cell">
        <div className="hero-triple-media">
          <span className="hero-proof-tag hero-proof-tag-trim">MotionTrim</span>
          <span className="hero-triple-sub">cuts the still stretches</span>
          <video
            ref={registerVideo}
            src="/landing-proof-after.mp4"
            poster={POSTER_TRIM}
            autoPlay={!reduceMotion}
            muted
            playsInline
            controls={showControls}
            preload="metadata"
            aria-label={`The same day after Tweedia MotionTrim removed still stretches, ${DUR_TRIM}`}
            data-testid="hero-triple-trim-video"
          />
          {!showControls && (
            <span className="hero-proof-dur" data-testid="hero-triple-dur-trim">
              {DUR_TRIM}
            </span>
          )}
        </div>
        <SourceTreatmentMap
          css={TRIM_MAP_CSS}
          label="still stretches removed"
          teal
        />
      </div>

      {/* Cell 3: MotionPace — imported through webpack gate */}
      <div className="hero-proof-cell hero-triple-cell">
        <div className="hero-triple-media">
          <span className="hero-proof-tag hero-proof-tag-after">MotionPace</span>
          <span className="hero-triple-sub">slows for the action, sprints past the quiet</span>
          <video
            ref={registerVideo}
            src={heroAfterClip}
            poster={heroAfterPoster}
            autoPlay={!reduceMotion}
            muted
            playsInline
            controls={showControls}
            preload="metadata"
            aria-label={`The same day with MotionPace adaptive pacing, ${DUR_PACE}`}
            data-testid="hero-triple-pace-video"
          />
          {!showControls && (
            <span className="hero-proof-dur" data-testid="hero-triple-dur-pace">
              {DUR_PACE}
            </span>
          )}
        </div>
        <SourceTreatmentMap
          css={PACE_MAP_CSS}
          label="every moment kept, speed varies"
          teal
        />
      </div>
    </div>
  );
}

/** Caption for the triple-hero — names all three modes and their durations. */
export function TripleHeroCaption() {
  return (
    <p className="hero-proof-caption">
      One day on a deck build, three ways. Straight off the camera it&apos;s <b>{DUR_BEFORE}</b>.{' '}
      <b>MotionTrim</b> cuts the quiet down to the <b>{DUR_TRIM}</b> that matters.{' '}
      <b>MotionPace</b> reaches the same <b>{DUR_PACE}</b> without dropping a moment — slow where
      the work happens, fast where nothing does.
    </p>
  );
}

/**
 * The MotionPace section, placed directly after the hero.
 *
 * The visual is the same motion chart + 45-cell frame-distribution strip from
 * the /motionpace page, extracted into a shared component (MotionChartHero) so
 * the SVG lives in one place.
 */
export function MotionPaceSection({ sectionRef, onLearnMoreClick }) {
  return (
    <section
      className="motionpace-section"
      aria-labelledby="motionpace-heading"
      ref={sectionRef}
      data-section-id={SECTION_ID}
      data-testid="homepage-motionpace-section"
    >
      <p className="section-kicker">New — MotionPace</p>
      <h2 id="motionpace-heading">The timelapse that paces itself</h2>
      <p className="section-lede">
        Constant speed gives every hour the same glance. MotionPace reads the motion in your
        shoot and spends the runtime where the action is — slowing into the busy stretches,
        sprinting past the quiet ones. Same length, twice the story.
      </p>
      <div className="motionpace-visual" data-testid="homepage-motionpace-chart">
        <MotionChartHero showFooter={false} />
        <p className="mp-panel-foot mp-panel-foot-home">
          <b>Every card is a frame that made the final cut, dropped at the minute it was shot.</b>{' '}
          Where the crew is working they pile up. Where the site is empty they spread apart. Same
          frames — speed follows the motion.
        </p>
      </div>
      <Link to="/motionpace" className="motionpace-link" onClick={onLearnMoreClick}>
        See how MotionPace works &rarr;
      </Link>
    </section>
  );
}

/**
 * Feature 1's demo panel: the before/after pair the hero used to show.
 *
 * Moving the clips here rather than dropping them keeps the motion-trim proof on
 * the page — it is the evidence for "cut the hours where nothing happens" — and
 * replaces a static chart image with the outcome that chart was describing.
 *
 * DELIBERATELY NOT ON THE HERO'S REF OR ITS BLOCKED STATE. This pair sits far
 * below the fold, and Safari / iOS Low Power Mode routinely refuse to autoplay a
 * clip that is off-screen. Sharing LandingPage's `proofVideoRef` would let that
 * refusal flip the hero's own `autoplayBlocked`, growing native control bars on
 * the hero and dropping its 0:29 / 0:12 badges — a visible change to the top of
 * the page caused by a video nobody has scrolled to. So this pair keeps its own
 * blocked state and starts on intersection rather than on mount; with
 * `preload="none"` and no `autoPlay`, four looping clips are never decoded at
 * once, and a refusal down here stays down here.
 *
 * `preload` goes back to `"metadata"` under reduced motion, though. That path
 * never calls `play()`, so nothing else would ever trigger a load — and since
 * `showControls` is then true, the 0:29 / 0:12 badges are hidden and the native
 * control bar is the only place the durations can appear. With no metadata it
 * would read 0:00 and the panel would prove nothing. Costs two header requests
 * on a path that was never going to decode the clips anyway.
 */
export function TrimProofPair({ reduceMotion }) {
  const [autoplayBlocked, setAutoplayBlocked] = useState(false);
  const gridRef = useRef(null);
  const videosRef = useRef([]);
  const showControls = reduceMotion || autoplayBlocked;

  const registerVideo = useCallback((el) => {
    if (!el) return;
    // Same prerender/hydration hole as the hero — this pair never carries
    // `autoplay`, but its intersection-triggered `play()` is refused just as
    // hard when `el.muted` is false. See forceMuted.
    forceMuted(el);
    if (!videosRef.current.includes(el)) videosRef.current.push(el);
  }, []);

  useEffect(() => {
    if (reduceMotion) return undefined;
    const play = () => {
      videosRef.current.forEach((el) => {
        el.play?.()?.catch?.(() => setAutoplayBlocked(true));
      });
    };
    // No IntersectionObserver (older browsers): play immediately rather than
    // leaving the visitor a pair of stills that never start.
    if (typeof IntersectionObserver === 'undefined') {
      play();
      return undefined;
    }
    const node = gridRef.current;
    if (!node) return undefined;
    const observer = new IntersectionObserver(
      (entries) => {
        if (!entries.some((entry) => entry.isIntersecting)) return;
        observer.disconnect();
        play();
      },
      { threshold: 0.25 }
    );
    observer.observe(node);
    return () => observer.disconnect();
  }, [reduceMotion]);

  return (
    <div className="feature-panel-media feature-panel-proof">
      <div className="hero-proof-grid hero-proof-grid--compact" ref={gridRef}>
        <div className="hero-proof-cell">
          <span className="hero-proof-tag hero-proof-tag-before">Before</span>
          <video
            ref={registerVideo}
            src="/landing-proof-before.mp4"
            poster="/landing-proof-before-poster.jpg"
            muted
            loop={!reduceMotion}
            playsInline
            controls={showControls}
            preload={reduceMotion ? 'metadata' : 'none'}
            aria-label="One day of a deck build straight off the camera, 29 seconds"
          />
          {!showControls && <span className="hero-proof-dur">0:29</span>}
        </div>
        <div className="hero-proof-cell">
          <span className="hero-proof-tag hero-proof-tag-after">Trimmed</span>
          <video
            ref={registerVideo}
            src="/landing-proof-after.mp4"
            poster="/landing-proof-after-poster.jpg"
            muted
            loop={!reduceMotion}
            playsInline
            controls={showControls}
            preload={reduceMotion ? 'metadata' : 'none'}
            aria-label="The same day after Tweedia trimmed the still stretches, 12 seconds"
          />
          {!showControls && <span className="hero-proof-dur">0:12</span>}
        </div>
      </div>
    </div>
  );
}

/** Feature 1's panel caption, describing the clips rather than the old chart. */
export function TrimProofCaption() {
  return (
    <p className="feature-panel-foot">
      One day on site: <b>0:29</b> of footage down to the <b>0:12</b> where something actually
      happens.
    </p>
  );
}
